import { useState } from 'react'
import { CheckCircle2, Lock } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { toast } from 'sonner'

interface QuickBantModalProps {
  open: boolean
  onClose: () => void
  dealId: string
  onSave: (updates: {
    budgetConfirmado: string
    decisorPresente: string
    doresIdentificadas: string
  }) => void | Promise<void>
  saving?: boolean
}

export function QuickBantModal({
  open,
  onClose,
  dealId: _dealId,
  onSave,
  saving = false,
}: QuickBantModalProps) {
  const [budget, setBudget] = useState('')
  const [decisor, setDecisor] = useState('')
  const [dores, setDores] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleConfirm = async () => {
    if (!budget) {
      toast.error('Informe se o budget está confirmado')
      return
    }
    if (!decisor) {
      toast.error('Informe se o decisor estava presente')
      return
    }
    if (!dores.trim()) {
      toast.error('Descreva as dores identificadas')
      return
    }

    setSubmitting(true)
    try {
      await onSave({
        budgetConfirmado: budget,
        decisorPresente: decisor,
        doresIdentificadas: dores,
      })
      onClose()
    } catch {
      toast.error('Erro ao salvar qualificação')
    } finally {
      setSubmitting(false)
    }
  }

  const isSaving = saving || submitting

  return (
    <Modal open={open} onClose={onClose} title="Qualificação Pós-Reunião" size="sm">
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-3 rounded-md bg-accent/5 border border-accent/10">
          <Lock size={18} className="text-accent shrink-0 mt-0.5" />
          <p className="text-xs text-text-label">
            Esta transição exige o preenchimento dos campos obrigatórios da qualificação BANT comercial.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">
            Budget Confirmado <span className="text-red-400">*</span>
          </label>
          <select
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            disabled={isSaving}
            className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-md px-3 py-2 outline-none focus:border-accent cursor-pointer"
          >
            <option value="">Selecionar...</option>
            <option value="Confirmado">Confirmado</option>
            <option value="A confirmar">A confirmar</option>
            <option value="Sem budget">Sem budget</option>
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">
            Decisor Presente <span className="text-red-400">*</span>
          </label>
          <select
            value={decisor}
            onChange={(e) => setDecisor(e.target.value)}
            disabled={isSaving}
            className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-md px-3 py-2 outline-none focus:border-accent cursor-pointer"
          >
            <option value="">Selecionar...</option>
            <option value="Sim">Sim</option>
            <option value="Parcial">Parcial</option>
            <option value="Não">Não</option>
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">
            Dores Identificadas <span className="text-red-400">*</span>
          </label>
          <textarea
            value={dores}
            onChange={(e) => setDores(e.target.value)}
            disabled={isSaving}
            rows={3}
            placeholder="Descreva as dores identificadas..."
            className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-md px-3 py-2 outline-none focus:border-accent resize-none"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isSaving} className="flex-1">
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<CheckCircle2 size={13} />}
            loading={isSaving}
            onClick={handleConfirm}
            className="flex-1"
          >
            Salvar e Avançar
          </Button>
        </div>
      </div>
    </Modal>
  )
}
